'use client';

import { LDClient, withLDConsumer } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { Client } from '../../../@types/cart-state';
import { useCartContext } from '../../cart-context';
import useAccountStep from '../../hooks/use-account-step';
import { CLIENT_CONVERTION_ERROR_KEY } from '../../hooks/use-convert-user-to-client';
import useEnterToSubmit from '../../hooks/use-enter-to-submit';
import useInsensitiveSearchParams from '../../hooks/use-insensitive-search-params';
import useQuoteData from '../../hooks/use-quote-data';
import ContinueButton from '../shared/continue-button';
import AccountStepFields from './account-step-fields';
import SwitchFormFooter from './switch-form-footer/switch-form-footer';

interface AccountStepProps {
  ldClient?: LDClient;
}

const getSessionStorageItem = (key: string): string => {
  return typeof window !== 'undefined' ? window.sessionStorage.getItem(key) ?? '' : '';
};

const getLocationSearch = (): string => {
  return typeof window !== 'undefined' ? window.location.search : '';
};

const getEmailError = (): boolean => {
  return getSessionStorageItem(`emailError-${getLocationSearch()}`) === 'true';
};

const getStoredEmail = (): string => {
  return getSessionStorageItem(`email-${getLocationSearch()}`);
};

const shouldSwitchToSignIn = (
  quoteData: any,
  conversionError: string,
  emailError: boolean,
  isClientState: boolean
): boolean => {
  const hasClientData = quoteData?.customer_type === 'client' || quoteData?.client_uuid;
  return !conversionError && !emailError && !isClientState && hasClientData;
};

function AccountStep({ ldClient }: AccountStepProps) {
  const leadParam = useInsensitiveSearchParams().get('lead_id') ?? '';
  const { loadingQuote, quoteData } = useQuoteData();
  const [isClientState, setIsClientState] = useState(false);

  const flag = ldClient?.variation(
    'MEX-12832-propagate-lead-uuid-to-checkout-on-quote-purchase',
    false
  );
  const lead_uuid = flag && !isClientState && !loadingQuote ? leadParam : '';
  const { cartState } = useCartContext();
  const emailError = getEmailError();
  const conversionError = getSessionStorageItem(CLIENT_CONVERTION_ERROR_KEY);

  const { control, handleSubmit, setError, register, setValue } = useForm<Client>({
    defaultValues: cartState.client || {
      email: getStoredEmail(),
    },
    mode: 'onBlur',
  });

  useEffect(() => {
    if (emailError) {
      setError('email', {
        message:
          "This email is already linked to a tutor or school account, which can't be used for purchases. To complete your checkout, please use a different email or contact Customer Service at 800-803-4058.",
      });
    }
    if (conversionError) {
      setError('email', {
        message:
          'It looks like this email is already connected to an account. Try logging in with the primary email to complete your purchase. If you need help updating your login email, contact Customer Service at 800-803-4058.',
      });
    }
  }, []);

  const { signInForm, loading, onSwitchForm, onSubmit } = useAccountStep({
    catalogItem: cartState.catalogItem,
    onContinue: () => {},
    setError,
  });

  const linkText = signInForm ? 'Create an account' : 'Sign in';
  const switchText = signInForm ? 'Not a user yet?' : 'Already have an account?';

  const submitParams = (client) => onSubmit(client, lead_uuid);
  useEnterToSubmit(handleSubmit(submitParams));

  useEffect(() => {
    if (!quoteData || loadingQuote) return;
    setValue('email', quoteData?.customer_email || '');
    setValue('lastName', quoteData?.customer_last_name || '');
    setValue('firstName', quoteData?.customer_first_name || '');

    if (shouldSwitchToSignIn(quoteData, conversionError, emailError, isClientState)) {
      setIsClientState(true);
      if (flag) {
        onSwitchForm();
      }
    }
  }, [quoteData, loadingQuote]);

  return (
    <>
      <div>
        <AccountStepFields control={control} register={register} signInForm={signInForm} />
      </div>
      <ContinueButton loading={loading || loadingQuote} onClick={handleSubmit(submitParams)} />
      <SwitchFormFooter linkText={linkText} onSwitch={onSwitchForm} text={switchText} />
    </>
  );
}

export default withLDConsumer()(AccountStep);
